import { buildShopifyAppScopeKey, getVerifiedShopifyScope, shopifyQueryKey } from '../ShopifyAppContext'
import { shopifyQueryPolicies } from '../queries/shopifyQueryPolicies'
/**
 * What: Loads aggregate Shopify store intelligence for the embedded app.
 * Transport / data-flow: Browser hook that calls protected Worker `/api/store-sync/status` through App Bridge ID-token fetches.
 *
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useShopifyScopeLifecycle } from './useShopifyScopeLifecycle'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAppBridge } from '@shopify/app-bridge-react'
import { productLanguage, shopifyComponentCopy } from '@testfeed/product-language'
import { throwMerchantFacingApiError } from '../shopifyApiError'
import { shopifyApiFetch } from '../shopifyApiFetch'
import { describeErrorForDiagnostics, parseContractPayload, resolveMerchantErrorMessage } from '../merchantErrors'
import { reportClientDiagnostic } from '../clientDiagnostics'
import { trackShopifyAction } from '../shopifyUiTracking'
import {
  shopSettingsSchema,
  type ShopSettings,
  type StoreSyncAccepted,
  type StoreSyncBuildAccepted,
  type StoreSyncStatus,
} from '@testfeed/shopify-contracts'
import { parseStoreSyncAcceptedResponse, parseStoreSyncStatusResponse } from '../storeSyncStatusAdapter'

type StoreIntelligenceOptions = {
  isTestFeedConnected?: boolean
  /** Only Settings shows the shop settings; other callers do not load them. */
  includeShopSettings?: boolean
  scope?: {
    shopDomain?: string | null
    orgId?: string | null
    connectionId?: string | null
  }
}

/** Keeps Store Intelligence fetching separate from study-builder workflow state. */
export function useStoreIntelligence(
  shopify: Parameters<typeof shopifyApiFetch>[0],
  options: StoreIntelligenceOptions = {},
) {
  const appBridge = useAppBridge()
  const queryClient = useQueryClient()
  const isTestFeedConnected = options.isTestFeedConnected !== false

  const shopDomain = options.scope?.shopDomain ?? 'no-shop'
  const orgId = options.scope?.orgId ?? null
  const connectionId = options.scope?.connectionId ?? null
  const enabled = isTestFeedConnected && !!getVerifiedShopifyScope(shopDomain) && !!orgId && !!connectionId
  const { token, isActive } = useShopifyScopeLifecycle(buildShopifyAppScopeKey({ shopDomain, orgId, connectionId }))
  const [localStateToken, setLocalStateToken] = useState(token)
  const [syncError, setSyncError] = useState<string | null>(null)

  useEffect(() => {
    setLocalStateToken(token)
    setSyncError(null)
  }, [token])

  const syncStatusQueryKey = shopifyQueryKey(shopDomain, orgId, connectionId, ['store-sync-status'])
  const shopSettingsQueryKey = shopifyQueryKey(shopDomain, orgId, connectionId, ['store-shop-settings'])

  const syncStatusQuery = useQuery<StoreSyncStatus, Error>({
    ...shopifyQueryPolicies.syncStatus,
    queryKey: syncStatusQueryKey,
    queryFn: async ({ signal }) => {
      const response = await shopifyApiFetch(shopify, '/api/store-sync/status', { init: { signal } })
      if (!response.ok) {
        return throwMerchantFacingApiError(response, 'store-sync-status')
      }
      const result = parseStoreSyncStatusResponse(await response.json())
      signal.throwIfAborted()
      return result
    },
    refetchInterval: (query) => {
      if (!enabled) return false
      // Outstanding current resource stages are folded into syncActive by the
      // adapter. Publication-pending/parked coverage is not running work.
      return query.state.data?.syncActive ? 20_000 : 15 * 60_000
    },
    refetchIntervalInBackground: false,
    enabled,
  })
  const { data: syncStatus, refetch: refetchSyncStatus } = syncStatusQuery

  // The synced shop settings for the Store Data display (#3095). Read-only projection of what a
  // build already persisted; `null` until the first build syncs them. Refreshed by the daily build
  // (and by the sync-completion invalidation below), never a live Shopify call from this surface.
  const { data: shopSettings } = useQuery<ShopSettings | null, Error>({
    ...shopifyQueryPolicies.intelligence,
    queryKey: shopSettingsQueryKey,
    queryFn: async ({ signal }) => {
      const response = await shopifyApiFetch(shopify, '/api/store-sync/shop-settings', { init: { signal } })
      if (!response.ok) {
        return throwMerchantFacingApiError(response, 'store-shop-settings')
      }
      const result = parseContractPayload('shopSettingsSchema', shopSettingsSchema.nullable(), await response.json())
      signal.throwIfAborted()
      return result
    },
    enabled: options.includeShopSettings === true && enabled,
  })

  const prevSyncStatusRef = useRef<{ token: string; status: StoreSyncStatus | null } | null>(null)

  useEffect(() => {
    if (!enabled || !isActive(token)) return
    const previous = prevSyncStatusRef.current?.token === token ? prevSyncStatusRef.current.status : null
    prevSyncStatusRef.current = { token, status: syncStatus ?? null }

    const stagesChanged = syncStatus?.buildProgress?.stages.some((stage) => {
      const before = previous?.buildProgress?.stages.find((candidate) => candidate.kind === stage.kind)
      return before && (before.state !== stage.state || before.usable !== stage.usable)
    })
    const syncSettled = previous?.syncActive === true && syncStatus?.syncActive === false
    if (stagesChanged || syncSettled) {
      // Refresh current data as individual resources arrive, not only when a
      // build publishes. Status is never a prerequisite for those reads.
      for (const resource of ['store-shop-settings', 'insights', 'opportunity-setups', 'store-knowledge']) {
        void queryClient.invalidateQueries({ queryKey: shopifyQueryKey(shopDomain, orgId, connectionId, [resource]) })
      }
    }
    if (previous?.syncActive && syncStatus?.status === 'failed') {
      if (syncStatus?.errorMessage) {
        setSyncError(syncStatus.errorMessage)
      }
      appBridge.toast?.show(productLanguage.shopify.sync.failed, { isError: true })
    }
  }, [enabled, token, isActive, queryClient, shopDomain, orgId, connectionId, syncStatus, appBridge])

  const syncMutation = useMutation({
    mutationFn: ({ forceBackfill }: { forceBackfill: boolean; token: string }) =>
      trackShopifyAction(shopify, forceBackfill ? 'resync' : 'start-sync', async () => {
        const response = await shopifyApiFetch(shopify, '/api/store-sync', {
          init: {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ forceBackfill }),
          },
        })

        if (!response.ok) return throwMerchantFacingApiError(response, 'store-sync')

        const payload = await response.json()
        return parseStoreSyncAcceptedResponse(payload)
      }),
    onMutate: () => {
      setSyncError(null)
      // Keep accepted data visible; mutation busy state already communicates the
      // request. Acceptance is not fabricated progress or a completed result.
      return { queryKey: syncStatusQueryKey }
    },
    onSuccess: async (_body: StoreSyncAccepted | StoreSyncBuildAccepted, variables, context) => {
      if (!isActive(variables.token)) {
        if (context) await queryClient.invalidateQueries({ queryKey: context.queryKey, refetchType: 'none' })
        return
      }
      if (isActive(variables.token)) {
        appBridge.toast?.show(productLanguage.shopify.sync.started)
        // The acceptance receipt is deliberately not a sync result. Final
        // coverage and publication come from the canonical status endpoint.
        await refetchSyncStatus()
      }
    },
    onError: async (error: Error, variables, context) => {
      console.error(describeErrorForDiagnostics(error))
      const message = resolveMerchantErrorMessage(error, shopifyComponentCopy.shopifyApiErrors.couldNotSyncStoreData018)
      if (!isActive(variables.token)) {
        if (context) await queryClient.invalidateQueries({ queryKey: context.queryKey, refetchType: 'none' })
        return
      }
      reportClientDiagnostic(shopify, { schema: 'shopify-store-sync', paths: ['sync_failed'] })
      if (isActive(variables.token)) {
        setSyncError(message)
        appBridge.toast?.show(productLanguage.shopify.sync.failed, { isError: true })
      }
      await refetchSyncStatus()
    },
  })
  const { mutateAsync: executeSync } = syncMutation
  const isMutationSyncing = syncMutation.isPending && syncMutation.variables?.token === token

  // Activity truth comes from the contract, never the status word: the backend
  // owns run knowledge (units holding a live run) and ships it as syncActive.
  // Parked yielded coverage reports inactive however the status reads.
  const isSyncActive = isMutationSyncing || syncStatus?.syncActive === true
  // Publication-pending alone is backend steady state (no run active), never a
  // sync in progress. Only an existing publication can be described as stale;
  // a first publication remains visibly on its way in the history grid.
  const isPublicationPending =
    syncStatus?.publicationPending === true &&
    syncStatus.coverage?.publicationId !== null &&
    syncStatus.coverage?.publicationId !== undefined &&
    !isSyncActive
  const isSyncing = isSyncActive

  const runSync = useCallback(
    async (forceBackfill = false) => {
      if (!enabled || isSyncActive) return
      await executeSync({ forceBackfill, token })
    },
    [executeSync, enabled, isSyncActive, token],
  )

  const effectiveSyncError = (localStateToken === token ? syncError : null) ?? syncStatus?.errorMessage ?? null

  return {
    refresh: refetchSyncStatus,
    runSync,
    syncError: effectiveSyncError,
    isSyncing,
    isPublicationPending,
    syncStage: syncStatus?.stage ?? null,
    syncStatus: syncStatus?.status ?? 'idle',
    syncStatusResponse: syncStatus ?? null,
    syncStatusQuery,
    shopSettings: shopSettings ?? null,
  }
}
